import { maybePropN, prop } from '@ipfabric/utils/fp/prop';
import { useState } from 'react';
import type { ComponentType, FC, JSX } from 'react';
import { useGraphContextSelectors } from '../GraphContext';
import './styles.css';
import type { GraphBreadcrumbProps } from 'components/Graph/GraphBreadcrumbs/GraphBreadcrumbProps';
import { CompareModeBreadcrumbItem } from 'components/Graph/GraphBreadcrumbs/breadcrumbTypes/CompareModeBreadcrumbItem';
import { IntentCheckBreadcrumbItem } from 'components/Graph/GraphBreadcrumbs/breadcrumbTypes/IntentCheckBreadcrumbItem';
import { selectPathWarnings } from 'components/Graph/model/selectors/graphMetadata/selectPathWarnings';
import { selectIntentRule } from 'components/Graph/model/selectors/overlay/selectIntentRule';
import { selectSnapshotToCompareId } from 'components/Graph/model/selectors/overlay/selectSnapshotToCompareId';
import { set } from 'fp-ts';
import { LoadedViewBreadcrumbItem } from './breadcrumbTypes/LoadedViewBreadcrumbItem';
import { NetworkPathBreadcrumbItem } from './breadcrumbTypes/NetworkPathBreadcrumbItem';
import { NetworkViewerModeBreadcrumbItem } from './breadcrumbTypes/NetworkViewerModeBreadcrumbItem';
import { PathLookupInstanceBreadcrumbItem } from './breadcrumbTypes/PathLookupInstanceBreadcrumbItem';
import { PathLookupModeBreadcrumbItem } from './breadcrumbTypes/PathLookupModeBreadcrumbItem';
import type { GraphBreadcrumbType } from './helpers/buildBreadcrumbs';
import { useGraphBreadcrumbs } from './useGraphBreadcrumbs';

const breadcrumbsComponentByType: {
  [Key in GraphBreadcrumbType]: ComponentType<
    GraphBreadcrumbProps & { content: { type: Key } }
  >;
} = {
  compareMode: CompareModeBreadcrumbItem,
  intentCheck: IntentCheckBreadcrumbItem,
  loadedView: LoadedViewBreadcrumbItem,
  networkViewer: NetworkViewerModeBreadcrumbItem,
  path: NetworkPathBreadcrumbItem,
  pathLookup: PathLookupModeBreadcrumbItem,
  pathLookupInstance: PathLookupInstanceBreadcrumbItem,
};

const Breadcrumb = (props: GraphBreadcrumbProps): JSX.Element => {
  const BreadcrumbItem = breadcrumbsComponentByType[props.content.type];
  // Type consistency of props is guaranteed by breadcrumbsComponentByType.
  return <BreadcrumbItem {...(props as any)} />;
};

export const GraphBreadcrumbs: FC = () => {
  const ctxProps = useGraphContextSelectors({
    graphMetadata: prop('graphMetadata'),
    intentRule: selectIntentRule,
    loadedViewName: maybePropN('loadViewDialog', 'loadedView', 'name'),
    pathWarnings: selectPathWarnings,
    snapshotId: prop('snapshotId'),
    snapshotToCompareId: selectSnapshotToCompareId,
    overlay: prop('overlay'),
  });
  const breadcrumbs = useGraphBreadcrumbs(
    ctxProps.graphMetadata?.parameters ?? null,
    ctxProps.snapshotId,
    ctxProps.overlay,
    ctxProps.loadedViewName,
    ctxProps.pathWarnings,
  );

  const [removalIndicatedForTypes, setRemovalIndicatedForTypes] = useState<
    Set<GraphBreadcrumbType>
  >(() => set.empty);

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <div className='breadcrumbs graph-breadcrumbs'>
      {breadcrumbs.map((breadcrumb, index) => (
        <Breadcrumb
          content={breadcrumb}
          removalIndicated={removalIndicatedForTypes.has(breadcrumb.type)}
          // biome-ignore lint/suspicious/noArrayIndexKey:
          key={index}
          onIndicateRemoveOthers={(types) => {
            setRemovalIndicatedForTypes(
              types.length ? new Set(types) : set.empty,
            );
          }}
        />
      ))}
    </div>
  );
};
